import EXIF from "exif-js";
import {Promise} from "es6-promise";
import Server from "../server";
import jQuery from "jquery";

export interface Attachment {
    name?: string;
    hash?: string;
}

interface Params {
    url: string;
    maxWidth?: number;
    maxHeight?: number;
}

/**
 * File uploader that has special treatment of image files.
 *
 * Non image files are just uploaded raw, as you'd expect.
 *
 * Image files have their EXIF data examined and are transformed as necessary for correct orientation.
 * Also they are resized to fit maximum width/height constraints.
 */
export class Uploader {

    private params: Params;
    private server: Server;

    constructor(params: Params, server: Server) {
        this.params = params;
        this.server = server;
    }

    public uploadFile(file: File) : Promise<Attachment> {

        const filename = file.name;

        if (/^image\//i.test(file.type)) {
            // Resize image

            const p = new Promise<Attachment>((resolve, reject) => {

                const reader = new FileReader();

                // Set up event listeners
                reader.onloadend = () => {
                    const process = this.processImage(reader.result, file.name, file.type);
                    process.then((data) => {
                        resolve(data);
                    });
                    process.catch((error) => {
                        reject(error);
                    })
                };

                reader.onerror = () => {

                    const process = this.uploadBlob(file, filename);

                    process.then((data) => {
                        resolve(data);
                    });

                    process.catch((error) => {
                        reject(error);
                    })

                };

                // Kick off process
                reader.readAsDataURL(file);
            });

            return p;

        } else {
            return this.uploadBlob(file, filename);
        }
    }

    private ajaxToPromise(ajax: JQuery.jqXHR) : Promise<any> {

        const p = new Promise((resolve, reject) => {
            ajax.then((data) => {
                resolve(data);
            });
            ajax.fail((data) => {
                reject(data);
            })
        });

        return p;
    }

    private processImage(dataURL: string | ArrayBuffer | null, name: string, type: string) : Promise<any> {

        console.log("processImage " + name + ", " + type);

        if (dataURL instanceof ArrayBuffer || !dataURL) {
            // Don't know how to process....
            return new Promise((resolve, reject) => {
                reject("Can't process dataURL");
            });
        }

        // Create image with no src to start
        const image = new Image();

        // ---------------------
        // Setup ready to receive image once loaded
        const uploader = this;
        const p = new Promise((resolve, reject) => {

            image.onload = () => {

                // @ts-ignore type declaration for EXIF appears to be wrong
                EXIF.getData(image, function () {

                    // @ts-ignore
                    const me = this;

                    const make = EXIF.getTag(me, "Make");
                    const model = EXIF.getTag(me, "Model");
                    const orientation = EXIF.getTag(me, "Orientation");

                    const width = image.width;
                    const height = image.height;

                    const maxHeight = uploader.params.maxHeight;
                    const maxWidth = uploader.params.maxWidth;

                    const ratioV = (maxHeight && height > maxHeight) ? maxHeight / height : 1;
                    const ratioH = (maxWidth && width > maxWidth) ? maxWidth / width : 1;

                    const ratio = Math.min(ratioV, ratioH);

                    const newHeight = height * ratio;
                    const newWidth = width * ratio;

                    // console.log("processImage make=" + make + "; model=" + model + "; orientation=" + orientation + "; width = " + width + "; height = " + height + "; newWidth = " + newWidth + "; newHeight = " + newHeight);

                    const canvas = uploader.drawImage(image, orientation, newWidth, newHeight);
                    const d = canvas.toDataURL("image/jpeg");

                    uploader.uploadDataURL(d, name)
                        .then((data) => {
                            console.log("Done uploading data URL");
                            resolve(data)
                        });
                });

            };

            image.onerror = (err) => {
                reject(err);
            };

        });
        // ---------------------

        // Trigger image load
        image.src = dataURL;

        return p;
    }

    private uploadDataURL(dataURL: string, filename: string) : Promise<Attachment> {
        console.log("uploading dataURL " + dataURL.substring(0, 10) + "...");

        var formData = new FormData();
        formData.append("dataURL", dataURL);
        formData.append("filename", filename);

        return this.uploadFormData(formData);
    }

    private uploadBlob(blob: Blob, filename: string) : Promise<Attachment> {
        console.log("uploading blob...");

        var formData = new FormData();
        formData.append("file", blob);
        formData.append("filename", filename);

        return this.uploadFormData(formData);
    }

    private uploadFormData(formData: FormData) : Promise<Attachment> {
        const out = this.server.ajax({
            url: this.params.url,
            data: formData,
            cache: false,
            contentType: false,
            processData: false,
            method: 'POST',
        });
        return out;
    }

    private drawImage(img: HTMLImageElement, orientation: any, width: number, height: number): HTMLCanvasElement {

        if (!/^[1-8]$/.test(orientation)) {
            orientation = 1;
        }

        let x = 0;
        let y = 0;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        if (ctx == null) {
            // Can't draw
            throw "Can't create canvas";
        }

        ctx.save();
        /*
        The following does not seem to work, gives wrongly oriented images.

        switch (+orientation) {
            // 1 = The 0th row is at the visual top of the image, and the 0th column is the visual left-hand side.
            case 1:
                break;

            // 2 = The 0th row is at the visual top of the image, and the 0th column is the visual right-hand side.
            case 2:
                ctx.translate(width, 0);
                ctx.scale(-1, 1);
                break;

            // 3 = The 0th row is at the visual bottom of the image, and the 0th column is the visual right-hand side.
            case 3:
                ctx.translate(width, height);
                ctx.rotate(180 / 180 * Math.PI);
                break;

            // 4 = The 0th row is at the visual bottom of the image, and the 0th column is the visual left-hand side.
            case 4:
                ctx.translate(0, height);
                ctx.scale(1, -1);
                break;

            // 5 = The 0th row is the visual left-hand side of the image, and the 0th column is the visual top.
            case 5:
                canvas.width = height;
                canvas.height = width;
                ctx.rotate(90 / 180 * Math.PI);
                ctx.scale(1, -1);
                break;

            // 6 = The 0th row is the visual right-hand side of the image, and the 0th column is the visual top.
            case 6:
                canvas.width = height;
                canvas.height = width;
                ctx.rotate(90 / 180 * Math.PI);
                ctx.translate(0, -height);
                break;

            // 7 = The 0th row is the visual right-hand side of the image, and the 0th column is the visual bottom.
            case 7:
                canvas.width = height;
                canvas.height = width;
                ctx.rotate(270 / 180 * Math.PI);
                ctx.translate(-width, height);
                ctx.scale(1, -1);
                break;

            // 8 = The 0th row is the visual left-hand side of the image, and the 0th column is the visual bottom.
            case 8:
                canvas.width = height;
                canvas.height = width;
                ctx.translate(0, width);
                ctx.rotate(270 / 180 * Math.PI);
                break;
        }
         */

        ctx.drawImage(img, x, y, width, height);
        ctx.restore();

        return canvas;

    }

}
